import {
	ChangeEvent,
	FormEvent,
	KeyboardEvent,
	MouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useInstantSearch, useSearchBox, useStats } from 'react-instantsearch'

import PromptBox from './PromptBox/PromptBox'
import { UseAutocompleteProps } from './SearchBox.types'
import { clientGetSearchSuggestions } from '@/client'
import debounce from 'lodash/debounce'
import { flushSync } from 'react-dom'
import isSupportedStreamingServiceLink from '@/utils/urlValidator'
import isValidPromptSearchTerm from './SearchBoxPromptFilters'
import logger from '@/client/utils/logger'
import { sendAnalytics } from '@audio-network/analytics'
import useAiSearchStore from '@/stores/aiSearchStore'
import { useSession } from '@/context/Session'

const AUTOCOMPLETE_CHARACTER_THRESHOLD = 2
const PROMPT_SEARCH_PREFIX = 'anw-promptsearch:'

export const useAutocomplete = (): UseAutocompleteProps => {
	const { user } = useSession()
	const { query, refine } = useSearchBox()
	const { nbHits } = useStats()
	const { status } = useInstantSearch()
	const inputRef = useRef(null)
	const canAutocomplete = useRef(true)
	const [autocompleteQuery, setAutocompleteQuery] = useState(null)
	const [activeDescendant, setActiveDescendant] = useState(null)
	const [apiOptions, setApiOptions] = useState(null)
	const [promptValue, setPromptValue] = useState('')
	const [isInitialRender, setIsInitialRender] = useState(true)
	const [failedTypeahead, setFailedTypeahead] = useState(null)

	const {
		prefetchSearchByAudio,
		prefetchedSearchByAudio,
		setSearchByAudio,
		removePrefetchedSearchByAudio,
		searchByPrompt,
		setSearchByPrompt,
		aiSearchResults,
		setAiSearchPending,
		clearAiSearch,
	} = useAiSearchStore()

	const listboxId = 'search-listbox'

	const sendPromptSearchAnalytics = (): void => {
		sendAnalytics({
			noun: 'Prompt Search',
			pastTenseVerb: 'Submitted',
		})
	}

	const createPromptSuggestion = useCallback(
		(value: string, showExplanation = false) => {
			if (!user) return null

			if (value.includes(prefetchedSearchByAudio?.title)) {
				return null
			}

			if (!isValidPromptSearchTerm(value)) {
				return null
			}

			return {
				label: <PromptBox prompt={value} showExplanation={showExplanation} />,
				value: `${PROMPT_SEARCH_PREFIX}${value}`,
			}
		},
		[user, prefetchedSearchByAudio]
	)

	/*
	 * The prompt suggestion option should show most up to date search value only keyword suggestions are debounced.
	 * The reason keyword suggestions are debounced is because we don't want to call the api too much.
	 */
	const options = useMemo(() => {
		const showPromptSuggestion =
			promptValue?.length >= AUTOCOMPLETE_CHARACTER_THRESHOLD && user
		const showPromptExplanation = !apiOptions?.length

		const promptSuggestion = showPromptSuggestion
			? createPromptSuggestion(promptValue, showPromptExplanation)
			: null

		if (promptSuggestion) {
			return [...(apiOptions || []), promptSuggestion]
		}
		return apiOptions || []
	}, [apiOptions, promptValue, user, createPromptSuggestion])

	const debouncedFetchRef = useRef(
		debounce(async (value: string, failedTypeahead) => {
			let suggestions = []
			if (
				!failedTypeahead ||
				!value.toLowerCase().startsWith(failedTypeahead)
			) {
				suggestions = await clientGetSearchSuggestions(value)
			}

			if (suggestions.length === 0 && !failedTypeahead) {
				setFailedTypeahead(value.toLowerCase())
			} else if (suggestions.length > 0 && failedTypeahead) {
				setFailedTypeahead(null)
			}

			setApiOptions(suggestions)
		}, 300)
	)

	const debouncedFetch = useCallback(
		(value: string) => {
			debouncedFetchRef.current(value, failedTypeahead)
			return () => debouncedFetchRef.current.cancel()
		},
		[failedTypeahead]
	)

	const fetchSuggestions = useCallback(
		(value: string) => {
			debouncedFetch(value)
		},
		[debouncedFetch]
	)

	const handleChange = async (
		event: ChangeEvent<HTMLInputElement>
	): Promise<void> => {
		const { value } = event.target as HTMLInputElement

		setPromptValue(value)

		if (
			value.length >= AUTOCOMPLETE_CHARACTER_THRESHOLD &&
			!isSupportedStreamingServiceLink(value) &&
			canAutocomplete.current
		) {
			fetchSuggestions(value)
		} else {
			setApiOptions(null)
			canAutocomplete.current = true
		}

		if (isSupportedStreamingServiceLink(value)) {
			setTimeout(() => {
				/** We need to allow the URL input to rerender before prefetching
				 * otherwise the input will appear empty until the prefetch is complete
				 * (the trade off is that the URL input will appear briefly before the prefetch is complete)
				 */
				prefetchSearchByAudio(value, !!user)
			})
		}
	}

	const handleKeyDown = (event: KeyboardEvent): void => {
		const { key, target } = event as Omit<KeyboardEvent, 'target'> & {
			target: HTMLInputElement
		}

		const { value } = target

		if (key === 'Backspace' && value === prefetchedSearchByAudio?.title) {
			event.preventDefault()
			setPromptValue('')
			removePrefetchedSearchByAudio()
			target.value = ''
		}

		if (!value) {
			setApiOptions(null)
			return
		}

		if (!options || options.length === 0) {
			return
		}

		const listbox = document.getElementById(
			target.getAttribute('aria-controls')
		)

		switch (key) {
			case 'ArrowDown':
				if (!activeDescendant && !autocompleteQuery) {
					setAutocompleteQuery(value)
					setActiveDescendant(options[0].value)
				} else if (activeDescendant === options[options.length - 1].value) {
					listbox.scrollTop = 0
					setActiveDescendant(null)
				} else {
					const index = options.findIndex(
						(option) => activeDescendant === option.value
					)
					if (index !== undefined && index !== -1) {
						setActiveDescendant(options[index + 1].value)
					}
				}
				break
			case 'ArrowUp':
				event.preventDefault()

				if (!activeDescendant && !autocompleteQuery) {
					setAutocompleteQuery(value)
					setActiveDescendant(options[options.length - 1].value)
					listbox.scrollTop = listbox.scrollHeight
				} else if (activeDescendant === options[0].value) {
					setActiveDescendant(null)
					listbox.scrollTop = 0
				} else {
					const index = options.findIndex(
						(option) => activeDescendant === option.value
					)
					if (index !== undefined && index !== -1) {
						setActiveDescendant(options[index - 1].value)
					}
				}
				break
			case 'Enter':
				setAutocompleteQuery(null)
				setActiveDescendant(null)
				setApiOptions(null)
				if (activeDescendant?.startsWith(PROMPT_SEARCH_PREFIX)) {
					event.preventDefault()
					event.stopPropagation()
					const prompt = activeDescendant.substring(PROMPT_SEARCH_PREFIX.length)
					setSearchByPrompt(prompt)
					sendPromptSearchAnalytics()
				} else if (activeDescendant) {
					sendAnalytics({
						noun: 'Typeahead',
						pastTenseVerb: 'Submitted',
					})
				}
				setPromptValue('')
				break
			case 'Escape':
				event.preventDefault()

				if (activeDescendant) {
					inputRef.current.value = autocompleteQuery
					setActiveDescendant(null)
				}

				setApiOptions(null)
				setPromptValue('')
				break
			default:
				setAutocompleteQuery(null)
				setActiveDescendant(null)
		}
	}

	const handleSubmit = useCallback(
		async (event: FormEvent): Promise<void> => {
			event.preventDefault()
			event.stopPropagation()

			canAutocomplete.current = false
			debouncedFetchRef.current.cancel()

			const value = inputRef?.current?.value.trim()

			if (!value) {
				clearAiSearch()
				refine('')
				return
			}

			const isAudioSearch =
				value === prefetchedSearchByAudio?.title ||
				value === prefetchedSearchByAudio?.url

			try {
				if (isAudioSearch) {
					setSearchByAudio(value)
				} else {
					flushSync(() => {
						setApiOptions(null)
						setActiveDescendant(null)
						setAutocompleteQuery(null)
					})

					refine(value)
					await clearAiSearch()
				}
			} catch (error) {
				logger('Error in search handleSubmit', error, {
					query: value,
				})
			}
		},
		[clearAiSearch, prefetchedSearchByAudio, refine, setSearchByAudio]
	)

	const handleOptionClick = (event: MouseEvent<HTMLButtonElement>): void => {
		const searchBox = document.querySelector(
			`[aria-controls=${listboxId}]`
		) as HTMLInputElement

		const target = event.target as HTMLElement

		const button =
			target.nodeName === 'BUTTON' ? target : target.closest('button')

		const { value } = button.dataset

		if (value?.startsWith(PROMPT_SEARCH_PREFIX)) {
			event.preventDefault()
			const prompt = value.substring(PROMPT_SEARCH_PREFIX.length)
			setSearchByPrompt(prompt)
			sendPromptSearchAnalytics()
			searchBox.value = prompt
			return
		} else {
			searchBox.value = options.find((option) => option.value === value).label
			sendAnalytics({
				noun: 'Typeahead',
				pastTenseVerb: 'Submitted',
			})
			handleSubmit(event as unknown as FormEvent)
		}
	}

	const handleBlur = (): void => {
		/**
		 * We use a timeout here,
		 * rather than check for the listbox as a relatedTarget,
		 * because Safari makes it impossible to check.
		 */
		setTimeout(() => {
			if (activeDescendant) {
				setActiveDescendant(null)
			}
			setApiOptions(null)
			setPromptValue('')
		}, 250)
	}

	/**
	 * Ensures that the listbox will scroll up and down
	 * when the user interacts with their keyboard
	 */

	const ensureOptionVisibility = useCallback((): void => {
		const optionsWrapper = document.getElementById(listboxId) as HTMLElement

		if (!activeDescendant) return

		const escapedActiveDescendant = CSS.escape(activeDescendant)

		const element = optionsWrapper.querySelector(
			`[data-value=${escapedActiveDescendant}]`
		) as HTMLElement

		if (!element) {
			return
		}

		const { offsetHeight, offsetTop } = element

		const {
			offsetHeight: optionsWrapperOffsetHeight,
			scrollTop: optionsWrapperScrollTop,
		} = optionsWrapper

		const isAbove = offsetTop < optionsWrapperScrollTop
		const isBelow =
			offsetTop + offsetHeight >
			optionsWrapperScrollTop + optionsWrapperOffsetHeight

		if (isAbove) {
			optionsWrapper.scrollTo(0, offsetTop)
		} else if (isBelow) {
			optionsWrapper.scrollTo(
				0,
				offsetTop - optionsWrapperOffsetHeight + offsetHeight
			)
		}
	}, [activeDescendant, listboxId])

	useEffect(() => {
		if (activeDescendant?.startsWith(PROMPT_SEARCH_PREFIX)) {
			const prompt = activeDescendant.substring(PROMPT_SEARCH_PREFIX.length)
			inputRef.current.value = prompt
			ensureOptionVisibility()
		} else if (activeDescendant) {
			const option = options.find(
				(option) => option.value === activeDescendant
			)?.label
			if (option) {
				inputRef.current.value = option
			}
			ensureOptionVisibility()
		} else if (!activeDescendant && autocompleteQuery) {
			inputRef.current.value = autocompleteQuery
			setAutocompleteQuery(null)
		}
	}, [activeDescendant, autocompleteQuery, ensureOptionVisibility, options])

	useEffect(() => {
		if (inputRef.current) {
			if (aiSearchResults?.length > 0 && query.length > 0) {
				refine('')
			}
			if (searchByPrompt || prefetchedSearchByAudio?.title) {
				inputRef.current.value =
					prefetchedSearchByAudio?.title ?? searchByPrompt
			} else {
				inputRef.current.value = query
			}
		}
	}, [aiSearchResults, searchByPrompt, query, prefetchedSearchByAudio, refine])

	useEffect(() => {
		if (
			!isInitialRender &&
			status === 'idle' &&
			query.length > 0 &&
			!prefetchedSearchByAudio
		) {
			setAiSearchPending(false)
		}

		setIsInitialRender(false)
	}, [
		isInitialRender,
		status,
		query,
		nbHits,
		prefetchedSearchByAudio,
		setSearchByPrompt,
		setAiSearchPending,
	])

	return {
		activeDescendant,
		canAutocomplete,
		handleChange,
		handleBlur,
		handleKeyDown,
		handleOptionClick,
		handleSubmit,
		iconName: prefetchedSearchByAudio?.service,
		inputRef,
		listboxId,
		options,
	}
}
